import { useTranslation } from "react-i18next";
import IconDonFilled from "@/assets/icons/don-filled.svg?react";
import IconChevron from "@/assets/icons/chevron.svg?react";
import { useNavigate } from "react-router";
import { useMemo } from "react";
import { calculateProgressPercentage, cn, formatNumber } from "@/shared/utils";
import { ChallengeStatus } from "@/pages/challenges/types";
import VibratingButton from "@/components/base/button/VibratingButton.tsx";

interface Props {
  id: string;
  logo: string;
  title: string;
  status: ChallengeStatus;
  targetAmount: string;
  gainedAmount: string;
  chance: number | string;
}

const STATUS_COLORS = {
  progressBar: {
    [ChallengeStatus.COMPLETED]: "bg-yellow-linear",
    [ChallengeStatus.DETERMINING_WINNERS]: "bg-yellow-linear",
    [ChallengeStatus.CANCELED]: "bg-red/40",
    [ChallengeStatus.STARTED]: "bg-green",
  } as Record<string, string>,
  text: {
    [ChallengeStatus.STARTED]: "text-green",
    [ChallengeStatus.CANCELED]: "text-gray-1",
    [ChallengeStatus.COMPLETED]: "text-orange-100",
  } as Record<string, string>,
  border: {
    [ChallengeStatus.COMPLETED]: "bg-yellow-linear p-px rounded-lg",
    [ChallengeStatus.CANCELED]: "border rounded-lg border-red",
  } as Record<string, string>,
};

const STATUS_ICONS: Record<string, string> = {
  [ChallengeStatus.CANCELED]: "error.svg",
  [ChallengeStatus.COMPLETED]: "completed.svg",
  [ChallengeStatus.STARTED]: "done.svg",
};

const getProgressBarColor = (status: ChallengeStatus): string => {
  return STATUS_COLORS.progressBar[status] || "bg-blue";
};

const getTextColor = (status: ChallengeStatus): string => {
  return STATUS_COLORS.text[status] || "";
};

const getBorderStyle = (status: ChallengeStatus): string => {
  return (
    STATUS_COLORS.border[status] || "border-b border-white/[0.16] rounded-lg"
  );
};

const getStatusIcon = (status: ChallengeStatus): string | null => {
  return STATUS_ICONS[status] || null;
};

const ParticipateCard = ({
  id,
  title,
  targetAmount,
  gainedAmount,
  status,
  chance,
}: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const progressPercentage = useMemo(() => {
    const completedStatuses = [
      ChallengeStatus.DETERMINING_WINNERS,
      ChallengeStatus.COMPLETED,
    ];
    if (completedStatuses.includes(status)) {
      return 100;
    }
    return calculateProgressPercentage(targetAmount, gainedAmount);
  }, [status, targetAmount, gainedAmount]);

  const leftDonAmount = useMemo(() => {
    return Math.max(parseFloat(targetAmount) - parseFloat(gainedAmount), 0);
  }, [targetAmount, gainedAmount]);

  const getSubtitleText = (status: ChallengeStatus): string => {
    const statusTexts: Record<string, string> = {
      [ChallengeStatus.ACTIVE]: t("challenge.need_don_for_super_win"),
      [ChallengeStatus.CANCELED]: t("challenge.cancelled_lack_of_don"),
      [ChallengeStatus.STARTED]: t("challenge.completed_gaining_don"),
      [ChallengeStatus.COMPLETED]: t("challenge_successfully_completed"),
      [ChallengeStatus.DETERMINING_WINNERS]: t(
        "challenge.challenge_result_determining"
      ),
    };
    return statusTexts[status] || "* not specified";
  };

  const shouldShowDonInfo = ![
    ChallengeStatus.CANCELED,
    ChallengeStatus.COMPLETED,
  ].includes(status);
  const chanceNumber = parseInt(chance.toString()) + 1;
  const statusIcon = getStatusIcon(status);

  const handleCardClick = () => {
    navigate(`/challenges/${id}`);
  };

  return (
    <VibratingButton className="text-start">
      <div
        onClick={handleCardClick}
        className={getBorderStyle(status)}
        data-testid="participate-card"
      >
        <div className="bg-gray-4 p-3 rounded-lg">
          {/* Header Section */}
          <div className="flex items-center">
            <div className="flex justify-between w-full items-center">
              <div>
                <p className="text-gray-1 leading-130 font-normal text-xs mb-0.5">
                  {t("challenge_name")}
                </p>
                <h1
                  className="text-base leading-130 text-white font-bold"
                  data-testid="challenge-title"
                >
                  {title}
                </h1>
              </div>
              <IconChevron className="text-gray-1" width="24" height="24" />
            </div>
          </div>

          {/* Divider */}
          <div className="w-full h-px bg-white/08 self-stretch my-3" />

          {/* Content Section */}
          <div>
            {/* Status and Don Info Row */}
            <div className="flex items-center justify-between">
              <p
                className={cn(
                  "flex items-center leading-130 justify-center gap-1 text-xs font-normal",
                  getTextColor(status)
                )}
                data-testid="status-text"
              >
                {statusIcon && (
                  <img
                    src={statusIcon}
                    alt={`${status} icon`}
                    data-testid="status-icon"
                  />
                )}
                {getSubtitleText(status)}
              </p>

              {shouldShowDonInfo && (
                <div className="flex items-center gap-1" data-testid="don-info">
                  <IconDonFilled
                    className="text-gray-1"
                    width="14"
                    height="14"
                  />
                  <span className="text-white font-medium text-xs leading-130 text-right">
                    {formatNumber(leftDonAmount)}
                  </span>
                </div>
              )}
            </div>

            {/* Progress Bar */}
            <div className="h-2 w-full rounded-lg bg-white/08 self-stretch my-3">
              <div
                className={cn(
                  "h-full w-full rounded-lg",
                  getProgressBarColor(status)
                )}
                style={{ width: `${progressPercentage}%` }}
                data-testid="progress-bar"
              />
            </div>

            {/* Chance Info */}
            <div className="flex-center-between">
              <p className="text-xs leading-130 font-normal text-gray-1">
                {t("number_of_opportunities_you_have")}
              </p>
              <p
                className="text-xs text-white leading-130 font-medium text-right lowercase"
                data-testid="chance-count"
              >
                {formatNumber(chanceNumber)} {t("chance")}
              </p>
            </div>
          </div>
        </div>
      </div>
    </VibratingButton>
  );
};

export default ParticipateCard;
